import { FC, ReactNode, useState } from 'react';

import { ContactHistoryKind } from '@/api/address-book/types';
import type { ContactHistoryAttachment, ContactHistoryEntry } from '@/api/address-book-bff/types';
import DownloadIcon from '@/assets/svg/ui-elements/download.svg';
import EditIcon from '@/assets/svg/ui-elements/edit-outline.svg';
import CategoryIcon from '@/assets/svg/ui-elements/layers.svg';
import NoteIcon from '@/assets/svg/ui-elements/notebook-text.svg';
import {
  Avatar,
  Badge,
  IconButton,
  Lightbox,
  resolveFileIcon,
  theme,
  Typography,
  useToast,
} from '@/shared/design-system';
import { useFileDownload } from '@/shared/hooks/use-file-download';
import { useIsOverflowing } from '@/shared/hooks/use-is-overflowing';
import { formatFileSize } from '@/utils/format-file-size';
import { isImageContentType } from '@/utils/is-image-content-type';

import {
  actorName,
  COMMUNICATION_KIND_BADGE_COLOR,
  COMMUNICATION_KIND_LABELS,
  isCommunicationKind,
} from './constants';
import {
  communicationTextOf,
  describeSystemEntry,
  describeSystemGroup,
  isEdited,
  type SystemEntryDescription,
} from './describe-entry';
import { isWithinContactHistoryEditWindow } from './edit-window';
import { formatEntryTime, formatEntryTimestamp } from './format-entry-time';
import { isBrowserViewable, openAttachmentInNewTab } from './open-attachment';
import {
  AttachmentChip,
  AttachmentChipAction,
  AttachmentChipMain,
  AttachmentChipName,
  AttachmentChips,
  AttachmentImage,
  AttachmentImageButton,
  AttachmentImages,
  EntryBody,
  EntryByline,
  EntryCard,
  EntryContent,
  EntryHeader,
  EntryNameRow,
  GroupDetails,
  InlineMeta,
  ShowMoreButton,
  SystemIconSlot,
  SystemRow,
  SystemText,
  ValueMark,
} from './styled';
import { useEntryEditWindow } from './use-entry-edit-window';

interface HistoryEntryProps {
  entry: ContactHistoryEntry;
  canEdit: boolean;
  editDisabled?: boolean;
  onEdit: (entry: ContactHistoryEntry) => void;
}

const isHistoryImageContentType = (contentType?: string): boolean =>
  isImageContentType(contentType) ||
  contentType?.split(';')[0].trim().toLowerCase() === 'image/heic';

export const EntryTime: FC<{ iso: string }> = ({ iso }) => (
  <Typography
    as="time"
    variant="body4"
    color={theme.colors.text.tertiary}
    dateTime={iso}
    title={formatEntryTimestamp(iso)}
  >
    {formatEntryTime(iso)}
  </Typography>
);

const Dot: FC = () => (
  <Typography as="span" variant="body4" color={theme.colors.text.tertiary}>
    ·
  </Typography>
);

export const AttachmentList: FC<{ attachments: ContactHistoryAttachment[] }> = ({
  attachments,
}) => {
  const [openImageId, setOpenImageId] = useState<string | null>(null);
  const [failedImageKeys, setFailedImageKeys] = useState<ReadonlySet<string>>(new Set());
  const { downloadFile } = useFileDownload();
  const { add: addToast } = useToast();

  if (attachments.length === 0) return null;

  const imageKey = (file: ContactHistoryAttachment): string =>
    JSON.stringify([file.fileId, file.url]);
  const isPreviewable = (file: ContactHistoryAttachment): boolean =>
    Boolean(file.url) &&
    isHistoryImageContentType(file.contentType) &&
    !failedImageKeys.has(imageKey(file));
  const images = attachments.filter(isPreviewable);
  const files = attachments.filter((file) => !isPreviewable(file));
  const openImage = attachments.find(
    (file) =>
      file.fileId === openImageId &&
      Boolean(file.url) &&
      isHistoryImageContentType(file.contentType),
  );

  const markImageFailed = (file: ContactHistoryAttachment): void => {
    setFailedImageKeys((current) => new Set(current).add(imageKey(file)));
    setOpenImageId((current) => (current === file.fileId ? null : current));
  };

  const view = (file: ContactHistoryAttachment, url: string) => {
    if (isImageContentType(file.contentType)) {
      setOpenImageId(file.fileId);
      return;
    }

    if (isBrowserViewable(file.contentType)) {
      openAttachmentInNewTab(url, file.contentType).catch(() =>
        addToast({
          title: "Couldn't open the file",
          description: 'Check your connection and try again, or download it instead.',
          data: { status: 'error' },
        }),
      );
      return;
    }

    void downloadFile(url, file.filename);
  };

  return (
    <>
      {images.length > 0 && (
        <AttachmentImages>
          {images.map((file) => (
            <AttachmentImageButton
              key={file.fileId}
              type="button"
              aria-label={`Open ${file.filename}`}
              title={file.filename}
              onClick={() => setOpenImageId(file.fileId)}
            >
              <AttachmentImage
                src={file.url!}
                alt={file.filename}
                onError={() => markImageFailed(file)}
              />
            </AttachmentImageButton>
          ))}
        </AttachmentImages>
      )}

      {files.length > 0 && (
        <AttachmentChips>
          {files.map((file) => {
            const Icon = resolveFileIcon(file.filename, file.contentType);
            const label = `${file.filename} · ${formatFileSize(file.size)}`;
            const { url } = file;
            const content = (
              <>
                <Icon />
                <AttachmentChipName>{file.filename}</AttachmentChipName>
                <Typography as="span" variant="body4" color={theme.colors.text.tertiary}>
                  {formatFileSize(file.size)}
                </Typography>
              </>
            );

            return (
              <AttachmentChip key={file.fileId}>
                {url ? (
                  <>
                    <AttachmentChipMain
                      type="button"
                      aria-label={`Open ${label}`}
                      onClick={() => view(file, url)}
                    >
                      {content}
                    </AttachmentChipMain>
                    <AttachmentChipAction
                      type="button"
                      aria-label={`Download ${file.filename}`}
                      title="Download"
                      onClick={() => void downloadFile(url, file.filename)}
                    >
                      <DownloadIcon />
                    </AttachmentChipAction>
                  </>
                ) : (
                  <AttachmentChipMain
                    as="span"
                    aria-label={label}
                    title="Download link unavailable"
                  >
                    {content}
                  </AttachmentChipMain>
                )}
              </AttachmentChip>
            );
          })}
        </AttachmentChips>
      )}

      {openImage?.url && (
        <Lightbox
          open
          onClose={() => setOpenImageId(null)}
          src={openImage.url}
          alt={openImage.filename}
          downloadUrl={openImage.url}
          downloadName={openImage.filename}
        />
      )}
    </>
  );
};

const SYSTEM_ICONS: Partial<Record<ContactHistoryEntry['kind'], ReactNode>> = {
  [ContactHistoryKind.FieldChanged]: <EditIcon width={16} height={16} />,
  [ContactHistoryKind.NoteChanged]: <NoteIcon width={16} height={16} />,
  [ContactHistoryKind.CategoryChanged]: <CategoryIcon width={16} height={16} />,
};

const ClampedBody: FC<{ text: string; muted?: boolean }> = ({ text, muted = false }) => {
  const [expanded, setExpanded] = useState(false);
  const { ref, isOverflowing } = useIsOverflowing<HTMLDivElement>([text, expanded]);

  return (
    <>
      <EntryContent ref={ref} expanded={expanded} muted={muted}>
        {text}
      </EntryContent>
      {(isOverflowing || expanded) && (
        <ShowMoreButton type="button" onClick={() => setExpanded((value) => !value)}>
          {expanded ? 'Show less' : 'Show more'}
        </ShowMoreButton>
      )}
    </>
  );
};

const SystemLine: FC<{ description: SystemEntryDescription; withActor?: boolean }> = ({
  description,
  withActor = true,
}) => (
  <span>
    {withActor && (
      <>
        <ValueMark>{description.actor}</ValueMark>{' '}
      </>
    )}
    {description.action}
    {description.value !== null && (
      <>
        {' '}
        <ValueMark>{description.value}</ValueMark>
      </>
    )}
  </span>
);

const SystemEntry: FC<{ entry: ContactHistoryEntry }> = ({ entry }) => {
  const description = describeSystemEntry(entry);

  return (
    <SystemRow data-entry-kind={entry.kind}>
      <SystemIconSlot>{SYSTEM_ICONS[entry.kind]}</SystemIconSlot>
      <SystemText>
        <InlineMeta>
          <SystemLine description={description} />
          <Dot />
          <EntryTime iso={entry.createdAt} />
        </InlineMeta>
        {description.body && <ClampedBody text={description.body} muted />}
      </SystemText>
    </SystemRow>
  );
};

export const HistorySystemGroup: FC<{ entries: ContactHistoryEntry[] }> = ({ entries }) => {
  const [expanded, setExpanded] = useState(false);
  const description = describeSystemGroup(entries);

  return (
    <SystemRow data-entry-group={entries.length}>
      <SystemIconSlot>{SYSTEM_ICONS[entries[0].kind]}</SystemIconSlot>
      <SystemText>
        <InlineMeta>
          <span>
            <ValueMark>{description.actor}</ValueMark> {description.action}
          </span>
          <Dot />
          <EntryTime iso={entries[0].createdAt} />
          <ShowMoreButton
            type="button"
            aria-expanded={expanded}
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? 'Hide details' : 'Show details'}
          </ShowMoreButton>
        </InlineMeta>
        {expanded && (
          <GroupDetails>
            {entries.map((entry) => {
              const line = describeSystemEntry(entry);
              return (
                <li key={entry.id}>
                  <SystemLine description={line} withActor={false} />
                  {line.body && <ClampedBody text={line.body} muted />}
                </li>
              );
            })}
          </GroupDetails>
        )}
      </SystemText>
    </SystemRow>
  );
};

const CommunicationEntry: FC<HistoryEntryProps> = ({
  entry,
  canEdit,
  editDisabled = false,
  onEdit,
}) => {
  const withinEditWindow = useEntryEditWindow(entry.createdAt);
  const name = actorName(entry.actor);
  const kind = isCommunicationKind(entry.kind) ? entry.kind : ContactHistoryKind.Note;
  const text = communicationTextOf(entry);
  const edited = isEdited(entry);

  return (
    <EntryCard as="article" data-entry-kind={entry.kind}>
      <EntryHeader>
        <Avatar size="sm" initials={name} />
        <EntryByline>
          <EntryNameRow>
            <Typography variant="body3" fontWeight={600}>
              {name}
            </Typography>
            <Badge
              label={COMMUNICATION_KIND_LABELS[kind]}
              color={COMMUNICATION_KIND_BADGE_COLOR[kind]}
              variant="subtle"
              size="sm"
            />
          </EntryNameRow>
          <InlineMeta>
            <EntryTime iso={entry.createdAt} />
            {edited && (
              <>
                <Dot />
                <Typography
                  as="span"
                  variant="body4"
                  color={theme.colors.text.tertiary}
                  title={`Edited ${formatEntryTimestamp(entry.updatedAt)}`}
                >
                  edited
                </Typography>
              </>
            )}
          </InlineMeta>
        </EntryByline>

        {canEdit && withinEditWindow && (
          <IconButton
            color="tertiary"
            variant="ghost"
            size="sm"
            aria-label="Edit entry"
            disabled={editDisabled}
            title={editDisabled ? 'Finish the open edit first' : undefined}
            onClick={() => {
              if (isWithinContactHistoryEditWindow(entry.createdAt)) onEdit(entry);
            }}
          >
            <EditIcon />
          </IconButton>
        )}
      </EntryHeader>

      {(text || entry.attachments.length > 0) && (
        <EntryBody>
          {text && <ClampedBody text={text} />}
          <AttachmentList key={entry.id} attachments={entry.attachments} />
        </EntryBody>
      )}
    </EntryCard>
  );
};

export const HistoryEntry: FC<HistoryEntryProps> = (props) =>
  isCommunicationKind(props.entry.kind) ? (
    <CommunicationEntry {...props} />
  ) : (
    <SystemEntry entry={props.entry} />
  );
